import React, { FC, useEffect, useRef, useState } from 'react';
import { Button, Checkbox } from 'antd';
import { FloatingActionBar, SearchInput, Spin } from 'common/components';
import { Cancelled, Edit } from 'common/svg';
import { LIST_LIMIT_0 } from 'common/config';
import { showSuccessMessage } from 'common/helpers';
import { useAppDispatch, useAppSelector } from 'app/store/store.hooks';
import { BrandCatalogsFilter, ManufacturerSelectModal } from 'entities/Account/components';
import { getBrandsMap } from 'entities/Account/Account.helper';
import {
  getAccountBrands,
  getAccounts,
  selectAccountBrands,
  selectAccountList,
  selectIsAccountLoading,
  updateAccountBrands,
} from 'entities/Account/Account.slice';
import { IUpdateAccountBrandsPayload } from 'entities/Account/Account.models';

export const BrandCatalogsPage: FC = () => {
  const dispatch = useAppDispatch();
  // account
  const accountList = useAppSelector(selectAccountList);
  const isAccountLoading = useAppSelector(selectIsAccountLoading);
  const accountBrands = useAppSelector(selectAccountBrands);

  const [fetchLoading, setFetchLoading] = useState<boolean>(true);
  const [selectedBrandIds, setSelectedBrandIds] = useState<number[]>([]);
  const [openManufacturerSelectModal, setOpenManufacturerSelectModal] = useState<boolean>(false);
  const letterRefs = useRef<Record<string, HTMLDivElement | null>>({});

  useEffect(() => {
    const fetch = async () => {
      await dispatch(getAccountBrands({}));
      await dispatch(getAccounts({ isSeller: true, limit: LIST_LIMIT_0 }));
      setFetchLoading(false);
    };

    fetch();
  }, []);

  const brandsMap = getBrandsMap(accountBrands);

  const onSearchClick = async (value?: string) => {
    await dispatch(getAccountBrands({ search: value }));
  };

  const changeBrandSelection = (id: number, checked: boolean) => {
    if (!checked) {
      setSelectedBrandIds((prev) => [...prev, id]);
    } else {
      setSelectedBrandIds((prev) => prev.filter((item) => item !== id));
    }
  };

  const clearSelection = () => {
    setSelectedBrandIds([]);
  };

  const scrollToLetter = (letter: string) => {
    const element = letterRefs.current[letter];

    if (!element) {
      return;
    }

    element.scrollIntoView({
      behavior: 'smooth',
      block: 'start',
    });
  };

  const openManufacturerChangeModal = () => {
    setOpenManufacturerSelectModal(true);
  };

  const closeManufacturerChangeModal = () => {
    setOpenManufacturerSelectModal(false);
  };

  const handleChangeManufacturer = ({ onSuccess, ...payload }: Omit<IUpdateAccountBrandsPayload, 'brands'>) => {
    const selectedBrands = accountBrands.filter((item) => selectedBrandIds.includes(item.id)).map((item: any) => item.brand);

    dispatch(
      updateAccountBrands({
        accountId: payload.accountId,
        brands: selectedBrands,
        onSuccess: () => {
          setSelectedBrandIds([]);
          showSuccessMessage('Производитель бренда изменен.');
          onSuccess?.();
        },
      }),
    );
  };

  if (fetchLoading) {
    return (
      <div className="h-full flex align-center justify-center">
        <Spin />
      </div>
    );
  }

  return (
    <div className="redesign relative p-11 pb-0 h-full flex direction-column overflow-hidden brand-catalogs">
      <div className="mb-13">
        <SearchInput placeholder="Введите название бренда" onSearch={onSearchClick} />
      </div>

      <div className="mb-9">
        <BrandCatalogsFilter onChange={scrollToLetter} />
      </div>

      <div className="flex-1 flex direction-column overflow-y-scroll scrollbar">
        {Array.from(brandsMap.entries()).map(([letter, brands]) => {
          if (!brands.length) {
            return null;
          }

          return (
            <div key={letter} className="mb-9 flex align-start brand-catalogs__block">
              <div
                ref={(el) => {
                  letterRefs.current[letter] = el;
                }}
                data-letter={letter}
                className="ff-regular brand-catalogs__block-letter"
              >
                {letter === 'other' ? '123' : letter}
              </div>

              <div className="brand-catalogs__block-brands">
                {brands.map((item) => {
                  const seller = accountList.find((i) => i.id === item.accountId);
                  const checked = selectedBrandIds.includes(item.id);

                  return (
                    <div key={item.id} className="flex align-center brand-catalogs__block-brands-item">
                      <Checkbox checked={checked} onChange={() => changeBrandSelection(item.id, checked)}>
                        {item.brand}
                      </Checkbox>
                      {seller && <span className="ff-regular fs-s color-dark-grey">{seller.name}</span>}
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>

      <FloatingActionBar selectedCount={selectedBrandIds.length} count={accountBrands.length}>
        <Button
          className="floating-action-bar__btn"
          icon={<Edit className="icon-dark-grey" />}
          onClick={openManufacturerChangeModal}
          loading={false}
        >
          Изменить производителя
        </Button>
        <Button
          className="floating-action-bar__btn red"
          icon={<Cancelled className="icon-red" />}
          onClick={clearSelection}
          loading={false}
        >
          Отменить выбор
        </Button>
      </FloatingActionBar>

      <ManufacturerSelectModal
        open={openManufacturerSelectModal}
        loading={isAccountLoading}
        sellers={accountList}
        onCancel={closeManufacturerChangeModal}
        onSubmit={handleChangeManufacturer}
      />
    </div>
  );
};
